<script setup lang="ts">
const { $metrics } = useNuxtApp();
const { login } = useAuthStore();
const { userPhone, userPhoneDdi } = useUserData();
const { userLoginData, twoFactorType, openLogin } = useAuth();
const { currentRouteName } = useRoutes();
const { userCanOnlyWithdraw } = useUserLimits();

const typedCode = ref('');
const isLoading = ref(false);

const isValid = computed<boolean>(() => {
  return typedCode.value.length === 5;
});

async function submit() {
  if (!userLoginData.value) {
    return;
  }

  isLoading.value = true;

  const loginData: ILoginSubmitData = {
    ...userLoginData.value,
    two_factor_code: Number(typedCode.value || 0),
  };

  await login(loginData)
    .then(async (response) => {
      $metrics.push('gtm')?.handle({
        event: 'login',
        ...gtmUserParams(response.data.user),
      });

      if (userCanOnlyWithdraw.value) {
        await navigateTo(localeLink('/user/wallet'));
      } else {
        await navigateTo(localeLink('/?deposit=1'));
      }

      if (currentRouteName.value === 'login') {
        if (userCanOnlyWithdraw.value) {
          await navigateTo(localeLink('/user/wallet'));
        } else {
          await navigateTo(localeLink('/'));
        }
      }
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function onSubmit() {
  submit();
}

function onConfirmButtonClick() {
  submit();
}

function onCancelButtonClick() {
  userLoginData.value = undefined;

  openLogin();
}

function start() {
  if (!twoFactorType.value || !userLoginData.value) {
    openLogin();
  }
}

onMounted(() => {
  start();
});

onActivated(() => {
  start();
});
</script>

<template>
  <section :class="$style.boxContainer">
    <BaseLoading
      class="!bg-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <p :class="$style.title">
      {{ $t(`auth.login.two_factor.${twoFactorType}.title`) }}
    </p>

    <p
      :class="$style.description"
      v-html="$t(`auth.login.two_factor.${twoFactorType}.desc`)"
    />

    <span :class="$style.number">
      {{ userPhoneDdi + userPhone }}
    </span>

    <BaseFormInputVerificationCode
      v-model="typedCode"
      @submit="onSubmit"
    />

    <p :class="$style.noCode">
      {{ $t(`auth.login.two_factor.${twoFactorType}.no_code`) }}
    </p>

    <div :class="$style.buttonsContainer">
      <button
        :class="[$style.submitBtn, 'btn-primary']"
        :disabled="!isValid"
        @click="onConfirmButtonClick"
      >
        {{ $t('verification.confirm_code') }}
        <NuxtIcon name="arrows/angle_right" />
      </button>

      <button
        :class="$style.textButton"
        @click.prevent="onCancelButtonClick"
      >
        {{ $t('auth.login.two_factor.cancel') }}
      </button>
    </div>
  </section>
</template>

<style module lang="scss">
.boxContainer {
  @apply p-3 md:p-6 flex flex-col justify-center flex-1 text-texts text-center;

  .title {
    @apply font-bold mb-3;
  }

  .description {
    @apply mb-1 max-w-80 inline-block mx-auto text-sm text-texts/80;

    strong {
      @apply text-primary/80;
    }
  }

  .number {
    @apply text-sm mb-6 font-bold text-texts/70 pointer-events-none;
  }

  .noCode {
    @apply mt-5 py-1 font-medium text-sm;
  }

  .textButton {
    @apply text-xs text-primary p-1 font-bold inline-flex mx-auto;

    &:disabled:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }

  .buttonsContainer {
    @apply flex flex-col items-center justify-center gap-2 mx-auto mt-5;

    button {
      @apply rounded-md gap-1.5 flex items-center font-bold uppercase h-10 focus:outline-none focus:ring-4;
    }

    .submitBtn {
      @apply py-5;
    }

    .backBtn {
      @apply bg-auth-texts/10 text-auth-texts/70 px-4 text-xs;
    }
  }
}
</style>
